import React, { useEffect, useMemo, useState } from "react";
import Modal from "./Modal.tsx";
import {
  getServiceUserMedicationGroups,
  getServiceUserSingleMedicationGroup,
} from "../../services/users.js";
import { updateAdministeredMedication } from "../../services/medication.js";
import toast from "react-hot-toast";

// CCS-377: lets an admin ADD an administered medication to a shift after it has
// ended (e.g. backfilling a record that was missed). The admin picks one of the
// service user's configured medications, then records how it was given. Writes
// through the same upsert endpoint as editing — with no existing row for this
// shift/medication, the backend inserts a new record (no duplicate).
const ADMINISTRATION_OPTIONS = [
  "Administered",
  "Prompted",
  "Declined",
  "Assisted",
  "Required",
  "Not Applicable",
];

// The medication config uses "N/A" as a placeholder dose; treat it as empty.
const cleanDose = (dose) => (dose && dose !== "N/A" ? dose : "");

export const AddAdministeredMedicationModal = ({
  isOpen,
  onClose,
  shiftId,
  serviceUserId,
  existingMedicationIds = [],
  onSaved,
}) => {
  const [groups, setGroups] = useState([]);
  const [loadingMeds, setLoadingMeds] = useState(true);
  const [medicationItemId, setMedicationItemId] = useState("");
  const [administrationType, setAdministrationType] = useState("Administered");
  const [signature, setSignature] = useState("");
  const [notes, setNotes] = useState("");
  const [dose, setDose] = useState("");
  const [error, setError] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  // Load the service user's medication groups, then each group's items via the
  // single-group endpoint — the groups list endpoint does not reliably include
  // nested medications, so this mirrors how UserMedications and
  // FieldShiftMedication populate item lists. Guarded with an `active` flag so
  // a late response never sets state after the modal closes/unmounts.
  useEffect(() => {
    if (!isOpen || !serviceUserId) return undefined;
    let active = true;
    setLoadingMeds(true);
    (async () => {
      try {
        const { data } = await getServiceUserMedicationGroups(serviceUserId);
        const groupList = Array.isArray(data) ? data : [];
        const withItems = await Promise.all(
          groupList.map(async (g) => {
            // A group may already carry its medications; only fetch when absent.
            if (Array.isArray(g.medications) && g.medications.length > 0) {
              return g;
            }
            try {
              const { data: detail } = await getServiceUserSingleMedicationGroup(
                serviceUserId,
                g.id
              );
              return { ...g, medications: detail?.medications || [] };
            } catch {
              // Deleted/unreadable group — skip its items but keep going.
              return { ...g, medications: [] };
            }
          })
        );
        if (active) setGroups(withItems);
      } catch (err) {
        console.error(err);
        if (active) setError("Failed to load medications");
      } finally {
        if (active) setLoadingMeds(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [isOpen, serviceUserId]);

  // Flatten items across all groups, dropping any already administered on this
  // shift (adding one of those would edit, not add) and any without an id.
  const options = useMemo(() => {
    const taken = new Set(existingMedicationIds.filter(Boolean));
    return groups.flatMap((g) =>
      (g.medications || [])
        .filter((m) => m.id && !taken.has(m.id))
        .map((m) => ({
          id: m.id,
          name: m.name,
          dose: m.dose,
          group: g.name,
        }))
    );
  }, [groups, existingMedicationIds]);

  const handleSelectMedication = (id) => {
    setMedicationItemId(id);
    const item = options.find((o) => o.id === id);
    // Prefill the dose from the medication's configured dose as a starting point.
    setDose(cleanDose(item?.dose));
  };

  const handleClose = () => {
    if (!isSaving) onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!medicationItemId) {
      setError("Please select a medication");
      return;
    }
    if (!signature.trim()) {
      setError("Signature is required");
      return;
    }
    setError("");

    let addedBy;
    try {
      addedBy = JSON.parse(localStorage.getItem("user"))?.username;
    } catch {
      addedBy = undefined;
    }

    const administrationDetails = {
      administrationType,
      signature: signature.trim(),
    };
    const trimmedNotes = notes.trim();
    if (trimmedNotes) administrationDetails.notes = trimmedNotes;
    if (addedBy) administrationDetails.administeredBy = addedBy;

    const payload = {
      shiftId: Number(shiftId),
      serviceUserId,
      medicationItemId,
      administrationDetails,
    };
    const trimmedDose = dose.trim();
    if (trimmedDose) payload.dose = trimmedDose;

    setIsSaving(true);
    try {
      await updateAdministeredMedication(payload, serviceUserId);
      toast.success("Medication added");
      onSaved?.();
      onClose();
    } catch (err) {
      console.error(err);
      toast.error("Failed to add medication");
      setIsSaving(false);
    }
  };

  const nothingToAdd = !loadingMeds && options.length === 0;

  return (
    <Modal
      title="Add administered medication"
      size="md"
      onCancel={handleClose}
      isOpen={isOpen}
    >
      <form onSubmit={handleSubmit}>
        <div className="form__group">
          <label htmlFor="addMedItem">
            Medication <span className="required">*</span>
          </label>
          <div className="form__input">
            <select
              id="addMedItem"
              value={medicationItemId}
              onChange={(e) => handleSelectMedication(e.target.value)}
              disabled={loadingMeds || nothingToAdd}
            >
              <option value="">
                {loadingMeds
                  ? "Loading medications..."
                  : nothingToAdd
                  ? "No medications available to add"
                  : "Select a medication"}
              </option>
              {options.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.group ? `${o.name} (${o.group})` : o.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="form__group">
          <label htmlFor="addMedAdminType">Administration type</label>
          <div className="form__input">
            <select
              id="addMedAdminType"
              value={administrationType}
              onChange={(e) => setAdministrationType(e.target.value)}
            >
              {ADMINISTRATION_OPTIONS.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="form__group">
          <label htmlFor="addMedDose">Dose</label>
          <div className="form__input">
            <input
              id="addMedDose"
              type="text"
              value={dose}
              onChange={(e) => setDose(e.target.value)}
              placeholder="e.g. 10mg"
            />
          </div>
        </div>

        <div className="form__group">
          <label htmlFor="addMedSignature">
            Signature <span className="required">*</span>
          </label>
          <div className="form__input">
            <input
              id="addMedSignature"
              type="text"
              value={signature}
              onChange={(e) => setSignature(e.target.value)}
              placeholder="Enter your full name"
            />
          </div>
        </div>

        <div className="form__group">
          <label htmlFor="addMedNotes">Notes</label>
          <div className="form__input">
            <textarea
              id="addMedNotes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Any additional observations or notes..."
            />
          </div>
        </div>

        {error && <span className="form__error">{error}</span>}
        <button
          type="submit"
          className="btn btn__primary btn--block"
          disabled={isSaving || loadingMeds || nothingToAdd}
        >
          {isSaving ? "Loading..." : "Add medication"}
        </button>
      </form>
    </Modal>
  );
};
